import { getStateFeeByCode, getEntityRenewalPrice } from './dummyData';
import { post } from '../../api';

const NMI_ENABLED = (process.env.REACT_APP_NMI_ENABLED || 'true') === 'true';
const NMI_TOKENIZATION_KEY = process.env.REACT_APP_NMI_TOKENIZATION_KEY || '';
const NMI_CHARGE_ENDPOINT = '/api/nmi/payment';

export function isNMIHostedEnabled() {
    return NMI_ENABLED;
}

export function getNMIConfigSummary() {
    return {
        enabled: NMI_ENABLED,
        tokenizationKeyConfigured: Boolean(NMI_TOKENIZATION_KEY),
        tokenizationKey: NMI_TOKENIZATION_KEY,
        chargeEndpoint: NMI_CHARGE_ENDPOINT,
    };
}

function isLikelyIOSMobileBrowser() {
    if (typeof navigator === 'undefined') {
        return false;
    }

    const ua = navigator.userAgent || '';
    const platform = navigator.platform || '';
    const maxTouchPoints = Number(navigator.maxTouchPoints || 0);
    return /iP(hone|od|ad)/i.test(ua) || (platform === 'MacIntel' && maxTouchPoints > 1);
}

function getNmiInputTextSize() {
    // iOS Safari auto-zooms focused inputs when computed font-size < 16px.
    // Keep larger text only where needed to avoid changing desktop/tablet visuals.
    return isLikelyIOSMobileBrowser() ? '16px' : '14px';
}


// https://guide.nmi.com/app/component-studio
export function getNmiAppearance() {
    const inputTextSize = getNmiInputTextSize();

    return {
        theme: 'light',
        layoutSpacing: 'compact',
        textSize: 'default',
        radiusSize: 'smaller',
        customTheme: {
            layout: {
                radius: {
                    small: '8px',
                    medium: '10px',
                    large: '12px',
                },
                borderWidth: {
                    small: '1px',
                    medium: '1px',
                    large: '1px',
                },
                spacing: {
                    padding: '0px',
                    xs: '4px',
                    sm: '8px',
                    md: '12px',
                    lg: '16px',
                    xl: '20px',
                    '2xl': '24px',
                    '3xl': '28px',
                },
            },
            typography: {
                size: {
                    xs: '12px',
                    sm: inputTextSize,
                    md: '14px',
                    lg: '14px',
                    xl: '14px',
                    '2xl': '16px',
                },
                weight: {
                    light: '400',
                    medium: '500',
                    bold: '700',
                },
                font: {
                    sans: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
                    mono: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace',
                },
            },
            colors: {
                background: '#ffffff',
                foreground: '#030B3D',
                border: '#d1d5db',
                input: '#f8fafc',
                primary: {
                    default: '#0156FC',
                    foreground: '#ffffff',
                    100: '#E7F0FF',
                    200: '#C8DCFF',
                },
                secondary: {
                    default: '#1f2937',
                    foreground: '#ffffff',
                    100: '#F3F4F6',
                    200: '#E5E7EB',
                },
                success: {
                    default: '#16a34a',
                    foreground: '#ffffff',
                    100: '#DCFCE7',
                    200: '#BBF7D0',
                },
                warning: {
                    default: '#d97706',
                    foreground: '#ffffff',
                    100: '#FEF3C7',
                    200: '#FDE68A',
                },
                danger: {
                    default: '#dc2626',
                    foreground: '#ffffff',
                    100: '#FEE2E2',
                    200: '#FECACA',
                },
                default: {
                    default: '#111827',
                    foreground: '#ffffff',
                    100: '#F3F4F6',
                    200: '#E5E7EB',
                },
                content1: {
                    default: '#0f172a',
                    foreground: '#ffffff',
                    100: '#F8FAFC',
                    200: '#F1F5F9',
                },
                content2: {
                    default: '#475569',
                    foreground: '#ffffff',
                    100: '#F8FAFC',
                    200: '#E2E8F0',
                },
            },
        },
    };
}

export function getCardLast4FromLookup(lookupData) {
    const masked = String(lookupData?.card?.number || '');
    const digits = masked.replace(/\D/g, '');
    return digits.slice(-4);
}

export function getCardExpiryFromLookup(lookupData) {
    const cardData = lookupData?.card || {};
    const month = String(cardData?.exp_month || cardData?.expiration_month || cardData?.expiry_month || '')
        .replace(/\D/g, '')
        .slice(0, 2);
    const yearRaw = String(cardData?.exp_year || cardData?.expiration_year || cardData?.expiry_year || '')
        .replace(/\D/g, '')
        .slice(0, 4);

    if (month && yearRaw) {
        return {
            expiryMonth: month,
            expiryYear: yearRaw,
        };
    }

    const expiry = String(cardData?.exp || cardData?.expiration || cardData?.expiry || '').trim();
    if (expiry.includes('/')) {
        const [monthPart = '', yearPart = ''] = expiry.split('/');
        return {
            expiryMonth: monthPart.replace(/\D/g, '').slice(0, 2),
            expiryYear: yearPart.replace(/\D/g, '').slice(0, 4),
        };
    }

    const digits = expiry.replace(/\D/g, '');
    if (digits.length === 4) {
        return {
            expiryMonth: digits.slice(0, 2),
            expiryYear: digits.slice(2),
        };
    }

    if (digits.length >= 6) {
        return {
            expiryMonth: digits.slice(0, 2),
            expiryYear: digits.slice(2, 6),
        };
    }

    return {
        expiryMonth: '',
        expiryYear: '',
    };
}

function dynamicProductFieldId(product) {
    const stableKey = product?.productCode || product?.id;
    return stableKey ? `cartProductSelection_${stableKey}` : '';
}

export function sumSelectedProductPrices(answers = {}, products = []) {
    if (!Array.isArray(products) || products.length === 0) return 0;
    let total = 0;
    for (const product of products) {
        if (!product || product.isActive === false) continue;
        const fieldId = dynamicProductFieldId(product);
        if (!fieldId) continue;
        const selectedValue = answers?.[fieldId];
        if (!selectedValue) continue;
        const option = (product.options || []).find((o) => (o.optionCode || o.id) === selectedValue);
        if (option && Number.isFinite(Number(option.price))) {
            total += Number(option.price) || 0;
        }
    }
    return total;
}

export function calculateCheckoutAmount(answers = {}, products = []) {
    const stateFormation = answers?.stateOfFormation || null;
    const stateCode = stateFormation?.stateCode || '';
    const stateFee = Number.isFinite(Number(stateFormation?.stateFee)) ? Number(stateFormation.stateFee) : getStateFeeByCode(stateCode);

    const promoTotalDue = Number(answers?.orderSummary?.promoTotalDue);
    if (answers?.orderSummary?.promoApplied && Number.isFinite(promoTotalDue)) {
        return Math.max(0, promoTotalDue);
    }
    const promoDiscount = answers?.orderSummary?.promoApplied ? Number(answers?.orderSummary?.promoDiscount || 0) : 0;

    let productsTotal;
    if (Array.isArray(products) && products.length > 0) {
        productsTotal = sumSelectedProductPrices(answers, products);
    } else {
        const hasRegisteredAgent = answers?.registeredAgent === 'add_registered_agent';
        const hasExpedited = answers?.expeditedProcessing === 'expedited';
        productsTotal = (hasRegisteredAgent ? 99 : 0) + (hasExpedited ? 49 : 0);
    }

    return Math.max(0, getEntityRenewalPrice() + stateFee + productsTotal - promoDiscount);
}

export async function chargeNMITokenOnBackend({ token, amount, orderContext, cardMeta, recordId, answerId }) {
    if (!token) {
        throw new Error('Missing payment token. Please go back and complete payment details.');
    }

    try {
        const data = await post('nmi/payment', {
            paymentToken: token,
            amount,
            context: orderContext || {},
            cardMeta: cardMeta || {},
            recordId: recordId || '',
            answerId: answerId || '',
        });

        if (!data?.success) {
            throw new Error(data?.message || 'Payment authorization failed.');
        }

        return data;
    } catch (error) {
        throw new Error(error?.message || 'Payment authorization failed.');
    }
}
